import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import AuthLayout from '@/components/AuthLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';

export default function AdminLogin() {
  const { login, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      if (user.role !== 'admin') {
        logout();
        toast.error('You do not have administrator access.');
        setLoading(false);
        return;
      }
      toast.success('Welcome back, Admin!');
      navigate('/admin');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout heading="Admin Login" subtitle="Sign in to access the control panel">
      <div className="p-8" style={{ backgroundColor: '#FFFFFF', border: '1px solid #000' }}>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label htmlFor="email" className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Email address</Label>
            <Input
              id="email" type="email" required placeholder="admin@example.com"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              className="h-11 rounded-none" style={{ borderColor: 'var(--border-clr)', backgroundColor: '#FDFCFA' }}
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label htmlFor="password" className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Password</Label>
              <Link to="/forgot-password" className="text-xs" style={{ color: 'var(--text-muted)', textDecoration: 'underline', textUnderlineOffset: '3px' }}>
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <Input
                id="password" type={showPass ? 'text' : 'password'} required placeholder="••••••••"
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                className="h-11 rounded-none" style={{ borderColor: 'var(--border-clr)', backgroundColor: '#FDFCFA', paddingRight: '2.75rem' }}
              />
              <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 transition-opacity hover:opacity-70" style={{ color: 'var(--text-muted)' }}>
                {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <Button
            type="submit" className="w-full h-12 flex items-center justify-center gap-2 mt-2 uppercase tracking-wider font-semibold" disabled={loading}
            style={{ backgroundColor: '#000', color: 'white', borderRadius: 0, fontSize: '0.8rem' }}
            onMouseEnter={(e) => { if (!loading) { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = '#000'; e.currentTarget.style.boxShadow = 'inset 0 0 0 1px #000'; } }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#000'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                  <path d="M12 2a10 10 0 0 1 10 10" />
                </svg>
                Signing in…
              </span>
            ) : (<><span>Sign in</span><ArrowRight size={15} /></>)}
          </Button>
        </form>
      </div>
      <div className="mt-8 flex items-center gap-3 justify-center">
        <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border-clr)' }} />
        <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Messums Art Market Administration</span>
        <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border-clr)' }} />
      </div>
    </AuthLayout>
  );
}
